import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout/Layout';
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';
import Loading from '../../components/Loading/Loading';
import ErrorMessage from '../../components/ErrorMessage/ErrorMessage';
import draftService from '../../services/draftService';
import { useUniverseForumsPath } from '../../hooks/useUniverseForumsPath';
import styles from './MyDrafts.module.css';

function formatDraftDate(value) {
  if (!value) return '';
  return new Date(value).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function excerptFromHtml(html, maxLength = 120) {
  if (!html) return '';
  const container = document.createElement('div');
  container.innerHTML = html;
  const text = (container.textContent || '').replace(/\s+/g, ' ').trim();
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength)}…`;
}

const MyDrafts = () => {
  const navigate = useNavigate();
  const forumsPath = useUniverseForumsPath();
  const [postDrafts, setPostDrafts] = useState([]);
  const [threadDrafts, setThreadDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionId, setActionId] = useState(null);

  const loadDrafts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await draftService.listDrafts();
      setPostDrafts(Array.isArray(data?.postDrafts) ? data.postDrafts : []);
      setThreadDrafts(Array.isArray(data?.threadDrafts) ? data.threadDrafts : []);
    } catch (err) {
      setError(err.response?.data?.error || 'Impossible de charger vos brouillons.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDrafts();
  }, [loadDrafts]);

  const handleDeletePostDraft = async (draft) => {
    if (!window.confirm('Supprimer ce brouillon de message ?')) return;
    setActionId(`post-${draft.id}`);
    try {
      await draftService.deletePostDraft(draft.id);
      await loadDrafts();
    } catch (err) {
      setError(err.response?.data?.error || 'Suppression impossible.');
    } finally {
      setActionId(null);
    }
  };

  const handleDeleteThreadDraft = async (draft) => {
    if (!window.confirm('Supprimer ce brouillon de discussion ?')) return;
    setActionId(`thread-${draft.id}`);
    try {
      await draftService.deleteThreadDraft(draft.id);
      await loadDrafts();
    } catch (err) {
      setError(err.response?.data?.error || 'Suppression impossible.');
    } finally {
      setActionId(null);
    }
  };

  const breadcrumbItems = [
    { name: 'PORTAIL', url: '/', icon: 'home' },
    { name: 'Mes brouillons', url: null, icon: 'forum' },
  ];

  return (
    <Layout wide>
      <div className={styles.content}>
        <Breadcrumb items={breadcrumbItems} />

        <header className={styles.header}>
          <h1 className={styles.title}>Mes brouillons</h1>
          <p className={styles.description}>
            Vos messages et discussions en cours de rédaction sont enregistrés automatiquement.
          </p>
        </header>

        {loading && <Loading message="Chargement de vos brouillons..." />}

        {!loading && error && (
          <ErrorMessage message={error} onRetry={loadDrafts} />
        )}

        {!loading && !error && (
          <>
            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <h2 className={styles.sectionTitle}>Messages</h2>
                <span className={styles.count}>{postDrafts.length}</span>
              </div>

              {postDrafts.length === 0 ? (
                <p className={styles.empty}>Aucun message en brouillon.</p>
              ) : (
                <ul className={styles.list}>
                  {postDrafts.map((draft) => (
                    <li key={`post-${draft.id}`} className={styles.item}>
                      <div className={styles.itemMain}>
                        <span className={styles.itemKind}>Message</span>
                        <h3 className={styles.itemTitle}>
                          {draft.threadTitle || 'Réponse en cours'}
                        </h3>
                        <p className={styles.itemMeta}>
                          {draft.forumName ? `${draft.forumName} • ` : ''}
                          Mis à jour le {formatDraftDate(draft.updatedAt)}
                        </p>
                        <p className={styles.itemExcerpt}>{excerptFromHtml(draft.content)}</p>
                      </div>
                      <div className={styles.itemActions}>
                        <Link
                          to={`/threads/${draft.threadSlug}`}
                          className={styles.primaryAction}
                        >
                          Reprendre
                        </Link>
                        <button
                          type="button"
                          className={styles.dangerAction}
                          disabled={actionId === `post-${draft.id}`}
                          onClick={() => handleDeletePostDraft(draft)}
                        >
                          Supprimer
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className={styles.section}>
              <div className={styles.sectionHeader}>
                <h2 className={styles.sectionTitle}>Discussions</h2>
                <span className={styles.count}>{threadDrafts.length}</span>
              </div>

              {threadDrafts.length === 0 ? (
                <p className={styles.empty}>Aucune discussion en brouillon.</p>
              ) : (
                <ul className={styles.list}>
                  {threadDrafts.map((draft) => (
                    <li key={`thread-${draft.id}`} className={styles.item}>
                      <div className={styles.itemMain}>
                        <span className={styles.itemKind}>Discussion</span>
                        <h3 className={styles.itemTitle}>{draft.title || 'Sans titre'}</h3>
                        <p className={styles.itemMeta}>
                          {draft.forumName ? `${draft.forumName} • ` : ''}
                          Mis à jour le {formatDraftDate(draft.updatedAt)}
                        </p>
                        <p className={styles.itemExcerpt}>{excerptFromHtml(draft.content)}</p>
                      </div>
                      <div className={styles.itemActions}>
                        <button
                          type="button"
                          className={styles.primaryAction}
                          onClick={() => {
                            const forumPath = draft.forumSlug
                              ? `/forums/${draft.forumSlug}?resumeDraft=${draft.id}`
                              : `${forumsPath}?resumeDraft=${draft.id}`;
                            navigate(forumPath);
                          }}
                        >
                          Reprendre
                        </button>
                        <button
                          type="button"
                          className={styles.dangerAction}
                          disabled={actionId === `thread-${draft.id}`}
                          onClick={() => handleDeleteThreadDraft(draft)}
                        >
                          Supprimer
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </div>
    </Layout>
  );
};

export default MyDrafts;
